import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  Alert,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { NumericFormat } from "react-number-format";
import dayjs from "dayjs";

import { useForm, Controller } from "react-hook-form";
import { ICreateBrokerageAnniversaryRequest } from "../../types/financial";
import {
  createBrokerageAnniversary,
  updateBrokerageAnniversary,
} from "../../store/modules/financialSettings/financialSettingsActions";
import { clearFinancialSettingsErrors } from "../../store/modules/financialSettings/financialSettingsStateActions";
import { IBrokerageAnniversaryModel } from "../../types/types";
import { RootState } from "../../store/reducers";

interface BrokerageAnniversaryFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ICreateBrokerageAnniversaryRequest) => void;
  initialData?: IBrokerageAnniversaryModel | null;
  loading?: boolean;
}

const BrokerageAnniversaryForm: React.FC<BrokerageAnniversaryFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  loading = false,
}) => {
  const dispatch = useDispatch();

  // Get error states from Redux
  const {
    createBrokerageAnniversary: createState,
    updateBrokerageAnniversary: updateState,
  } = useSelector((state: RootState) => state.financialSettings);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ICreateBrokerageAnniversaryRequest>({
    defaultValues: {
      name: "",
      anniversaryDate: undefined,
      commissionPercentage: 0,
      flatCommissionFee: 0,
      brokerageSplit: 0,
      splitCap: undefined,
      additionalChargePercentage: 0,
      additionalChargeCap: undefined,
      flatTransactionalFees: 0,
      referralOutFeeReceived: 25,
      referralFeePaid: 25,
      rentalCommissionPercentage: 50,
    },
  });

  useEffect(() => {
    if (initialData) {
      reset({
        name: initialData.name!,
        anniversaryDate: initialData.anniversaryDate
          ? dayjs.isDayjs(initialData.anniversaryDate)
            ? initialData.anniversaryDate
            : dayjs(initialData.anniversaryDate)
          : undefined,
        commissionPercentage: initialData.commissionPercentage!,
        flatCommissionFee: initialData.flatCommissionFee!,
        brokerageSplit: initialData.brokerageSplit!,
        splitCap: initialData.splitCap,
        additionalChargePercentage: initialData.additionalChargePercentage!,
        additionalChargeCap: initialData.additionalChargeCap,
        flatTransactionalFees: initialData.flatTransactionalFees!,
        referralOutFeeReceived: initialData.referralOutFeeReceived ?? 25,
        referralFeePaid: initialData.referralFeePaid ?? 25,
        rentalCommissionPercentage:
          initialData.rentalCommissionPercentage ?? 50,
      });
    } else {
      reset({
        name: "",
        anniversaryDate: undefined,
        commissionPercentage: 0,
        flatCommissionFee: 0,
        brokerageSplit: 0,
        splitCap: undefined,
        additionalChargePercentage: 0,
        additionalChargeCap: undefined,
        flatTransactionalFees: 0,
        referralOutFeeReceived: 25,
        referralFeePaid: 25,
        rentalCommissionPercentage: 50,
      });
    }
  }, [initialData, reset, open]);

  const onFormSubmit = (data: ICreateBrokerageAnniversaryRequest) => {
    if (initialData) {
      dispatch(
        updateBrokerageAnniversary(
          initialData.brokerageAnniversaryId!,
          data
        ) as any
      ).then((result: any) => {
        if (!result.error) {
          onSubmit(data);
        }
      });
    } else {
      dispatch(createBrokerageAnniversary(data) as any).then((result: any) => {
        if (!result.error) {
          onSubmit(data);
        }
      });
    }
  };

  // Handle form close and clear errors
  const handleClose = () => {
    dispatch(clearFinancialSettingsErrors());
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialData ? "Edit Brokerage Contract" : "Add Brokerage Contract"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {/* Display error messages */}
          {(createState.error || updateState.error) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createState.error || updateState.error}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="name"
                control={control}
                rules={{ required: "Name is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={
                      <span>
                        Name <span style={{ color: "red" }}>*</span>
                      </span>
                    }
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                      input: {
                        notched: true,
                      },
                    }}
                    placeholder="e.g., 2024 Plan"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="anniversaryDate"
                control={control}
                rules={{ required: "Starting Date is required" }}
                render={({ field }) => (
                  <DatePicker
                    label={
                      <span>
                        Starting Date <span style={{ color: "red" }}>*</span>
                      </span>
                    }
                    value={field.value}
                    onChange={(date) => {
                      // Ensure the date is valid before passing it to field.onChange
                      if (date && dayjs.isDayjs(date) && date.isValid()) {
                        field.onChange(date);
                      } else if (date === null) {
                        field.onChange(undefined);
                      }
                    }}
                    slotProps={{
                      textField: {
                        onBlur: field.onBlur,
                        fullWidth: true,
                        // error: !!error,
                        error: !!errors.anniversaryDate,
                        helperText: errors.anniversaryDate?.message,

                        // helperText: error?.message,
                        InputLabelProps: {
                          shrink: true,
                          sx: {
                            backgroundColor: "background.paper",
                            px: 1,
                            // "& .MuiOutlinedInput-notchedOutline": {
                            //   border: "none",
                            // },
                          },
                        },
                      },

                      field: { onBlur: () => field.onBlur(), clearable: true },
                      popper: {
                        // onClose: () => field.onBlur(),
                        disablePortal: true,
                      },
                    }}
                    // slotProps={{
                    //   textField: {
                    //     fullWidth: true,
                    //     error: !!errors.anniversaryDate,
                    //     helperText: errors.anniversaryDate?.message,
                    //   },
                    // }}
                  />
                )}
              />
            </Grid>

            {/* Note to users about auto-population in transactions */}
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  All fields below auto-populate in the transactions based on
                  contract date, and are editable per transaction
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="commissionPercentage"
                control={control}
                rules={{
                  min: {
                    value: 0,
                    message: "Commission Percentage must be at least 0%",
                  },
                  max: {
                    value: 100,
                    message: "Commission Percentage cannot exceed 100%",
                  },
                }}
                render={({ field }) => (
                  <NumericFormat
                    fullWidth
                    valueIsNumericString
                    onValueChange={(values) =>
                      field.onChange(values.floatValue)
                    }
                    customInput={TextField}
                    thousandSeparator={false}
                    allowLeadingZeros={false}
                    decimalScale={2}
                    onBlur={field.onBlur}
                    value={field.value ?? 0}
                    label="Commission Percentage (Ex: 3%)"
                    error={!!errors.commissionPercentage}
                    helperText={errors.commissionPercentage?.message}
                    allowNegative={false}
                    isAllowed={(values) => {
                      const { floatValue } = values;
                      return (
                        floatValue === undefined ||
                        (floatValue >= 0 && floatValue <= 100)
                      );
                    }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">%</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="flatCommissionFee"
                control={control}
                rules={{
                  min: {
                    value: 0,
                    message: "Flat Commission must be at least $0",
                  },
                }}
                render={({ field }) => (
                  <NumericFormat
                    fullWidth
                    valueIsNumericString
                    onValueChange={(values) =>
                      field.onChange(values.floatValue)
                    }
                    customInput={TextField}
                    thousandSeparator
                    allowLeadingZeros={false}
                    decimalScale={0}
                    onBlur={field.onBlur}
                    value={field.value ?? 0}
                    label="Flat Commission (Ex: $450)"
                    error={!!errors.flatCommissionFee}
                    helperText={errors.flatCommissionFee?.message}
                    allowNegative={false}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="brokerageSplit"
                control={control}
                rules={{
                  min: {
                    value: 0,
                    message: "Brokerage Split must be at least 0%",
                  },
                  max: {
                    value: 100,
                    message: "Brokerage Split cannot exceed 100%",
                  },
                }}
                render={({ field }) => (
                  <NumericFormat
                    fullWidth
                    valueIsNumericString
                    onValueChange={(values) =>
                      field.onChange(values.floatValue)
                    }
                    customInput={TextField}
                    thousandSeparator={false}
                    allowLeadingZeros={false}
                    decimalScale={0}
                    onBlur={field.onBlur}
                    value={field.value ?? 0}
                    label="Brokerage Split (Ex: 20%)"
                    error={!!errors.brokerageSplit}
                    helperText={errors.brokerageSplit?.message}
                    allowNegative={false}
                    isAllowed={(values) => {
                      const { floatValue } = values;
                      return (
                        floatValue === undefined ||
                        (floatValue >= 0 && floatValue <= 100)
                      );
                    }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">%</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="splitCap"
                control={control}
                rules={{
                  min: {
                    value: 0,
                    message: "Split Cap must be at least $0",
                  },
                }}
                render={({ field }) => (
                  <NumericFormat
                    fullWidth
                    valueIsNumericString
                    onValueChange={(values) => {
                      const value =
                        values.floatValue === undefined
                          ? undefined
                          : values.floatValue;
                      field.onChange(value);
                    }}
                    customInput={TextField}
                    thousandSeparator
                    allowLeadingZeros={false}
                    decimalScale={0}
                    onBlur={field.onBlur}
                    value={field.value ?? ""}
                    label="Split Cap (Optional)"
                    error={!!errors.splitCap}
                    helperText={errors.splitCap?.message}
                    allowNegative={false}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      },
                    }}
                    placeholder="Enter amount or leave empty for N/A"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="additionalChargePercentage"
                control={control}
                rules={{
                  min: {
                    value: 0,
                    message: "Additional Charge must be at least 0%",
                  },
                  max: {
                    value: 100,
                    message: "Additional Charge cannot exceed 100%",
                  },
                }}
                render={({ field }) => (
                  <NumericFormat
                    fullWidth
                    valueIsNumericString
                    onValueChange={(values) =>
                      field.onChange(values.floatValue)
                    }
                    customInput={TextField}
                    thousandSeparator={false}
                    allowLeadingZeros={false}
                    decimalScale={0}
                    onBlur={field.onBlur}
                    value={field.value ?? 0}
                    label="Additional % Brokerage Charge (Ex: 5%)"
                    error={!!errors.additionalChargePercentage}
                    helperText={
                      errors.additionalChargePercentage?.message ??
                      "Royalty, technology, or other fees."
                    }
                    allowNegative={false}
                    isAllowed={(values) => {
                      const { floatValue } = values;
                      return (
                        floatValue === undefined ||
                        (floatValue >= 0 && floatValue <= 100)
                      );
                    }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">%</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="additionalChargeCap"
                control={control}
                rules={{
                  min: {
                    value: 0,
                    message:
                      "Cap of Additional % Brokerage Charge must be at least $0",
                  },
                }}
                render={({ field }) => (
                  <NumericFormat
                    fullWidth
                    valueIsNumericString
                    onValueChange={(values) => {
                      const value =
                        values.floatValue === undefined
                          ? undefined
                          : values.floatValue;
                      field.onChange(value);
                    }}
                    customInput={TextField}
                    thousandSeparator
                    allowLeadingZeros={false}
                    decimalScale={0}
                    onBlur={field.onBlur}
                    value={field.value ?? ""}
                    label="Cap of Additional % Brokerage Charge (Optional)"
                    error={!!errors.additionalChargeCap}
                    helperText={errors.additionalChargeCap?.message}
                    allowNegative={false}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      },
                    }}
                    placeholder="Enter amount or leave empty for N/A"
                  />
                )}
              />
            </Grid>

            {/* <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Additional charges</strong> could be Royalty,
                  technology, or other fees.
                </Typography>
              </Alert>
            </Grid> */}

            <Grid item xs={12} md={6}>
              <Controller
                name="referralOutFeeReceived"
                control={control}
                rules={{
                  min: {
                    value: 0,
                    message: "Referral Out Fee Received must be at least 0%",
                  },
                  max: {
                    value: 100,
                    message: "Referral Out Fee Received cannot exceed 100%",
                  },
                }}
                render={({ field }) => (
                  <NumericFormat
                    fullWidth
                    valueIsNumericString
                    onValueChange={(values) =>
                      field.onChange(values.floatValue)
                    }
                    customInput={TextField}
                    allowLeadingZeros={false}
                    decimalScale={2}
                    onBlur={field.onBlur}
                    value={field.value ?? 25}
                    label="Referral Out Fee Received"
                    error={!!errors.referralOutFeeReceived}
                    helperText={errors.referralOutFeeReceived?.message}
                    allowNegative={false}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="referralFeePaid"
                control={control}
                rules={{
                  min: {
                    value: 0,
                    message: "Agent Referral Fee Paid must be at least 0%",
                  },
                  max: {
                    value: 100,
                    message: "Agent Referral Fee Paid cannot exceed 100%",
                  },
                }}
                render={({ field }) => (
                  <NumericFormat
                    fullWidth
                    valueIsNumericString
                    onValueChange={(values) =>
                      field.onChange(values.floatValue)
                    }
                    customInput={TextField}
                    allowLeadingZeros={false}
                    decimalScale={2}
                    onBlur={field.onBlur}
                    value={field.value ?? 25}
                    label="Agent Referral Fee Paid"
                    error={!!errors.referralFeePaid}
                    helperText={errors.referralFeePaid?.message}
                    allowNegative={false}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="rentalCommissionPercentage"
                control={control}
                rules={{
                  min: {
                    value: 0,
                    message: "Rental Commission Percentage must be at least 0%",
                  },
                  max: {
                    value: 100,
                    message: "Rental Commission Percentage cannot exceed 100%",
                  },
                }}
                render={({ field }) => (
                  <NumericFormat
                    fullWidth
                    valueIsNumericString
                    onValueChange={(values) =>
                      field.onChange(values.floatValue)
                    }
                    customInput={TextField}
                    allowLeadingZeros={false}
                    decimalScale={2}
                    onBlur={field.onBlur}
                    value={field.value ?? 50}
                    label="Rental Commission Percentage"
                    error={!!errors.rentalCommissionPercentage}
                    helperText={errors.rentalCommissionPercentage?.message}
                    allowNegative={false}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="flatTransactionalFees"
                control={control}
                rules={{
                  min: {
                    value: 0,
                    message:
                      "Flat Brokerage Transactional Fees must be at least $0",
                  },
                }}
                render={({ field }) => (
                  <NumericFormat
                    fullWidth
                    valueIsNumericString
                    onValueChange={(values) =>
                      field.onChange(values.floatValue)
                    }
                    customInput={TextField}
                    thousandSeparator
                    allowLeadingZeros={false}
                    decimalScale={0}
                    onBlur={field.onBlur}
                    value={field.value ?? 0}
                    label="Flat Brokerage Transactional Fees (No Cap)"
                    error={!!errors.flatTransactionalFees}
                    helperText={errors.flatTransactionalFees?.message}
                    allowNegative={false}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(onFormSubmit)}
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : null
          }
          sx={{
            backgroundColor: "primary.main",
            "&:hover": {
              backgroundColor: "primary.dark",
            },
          }}
        >
          {loading ? "Saving..." : initialData ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BrokerageAnniversaryForm;
